<script setup lang="ts">
import { SaleSteppedPrice } from '@lib/shared';

// PROPS
interface Props {
    disabled?: boolean;
    maxBidStepAmount: number;
    minBidStepAmount: number;
    minBidStepAmountThreshold: number;
    minValue?: number;
    maxValue?: number;
    steppedLimits?: boolean; // Apply step rounding on minValue/maxValue
}

const {
    disabled = false,
    maxBidStepAmount,
    minBidStepAmount,
    minBidStepAmountThreshold,
    minValue = 0,
    maxValue = Infinity,
    steppedLimits = false,
} = defineProps<Props>();

// MODEL
const model = defineModel<number>();

// COMPOSABLES
const { tn } = useTypedI18n();

// DATA
const saleSteppedPrice = computed(() => {
    return new SaleSteppedPrice(minBidStepAmount, minBidStepAmountThreshold, maxBidStepAmount);
});

const min = computed(() => {
    return steppedLimits ? saleSteppedPrice.value.getStepRoundedReservePrice(minValue) : minValue;
});

const max = computed(() => {
    return steppedLimits ? saleSteppedPrice.value.getStepRoundedAmount(maxValue) : maxValue;
});

const minusDisabledByValue = computed(() => model.value !== undefined && model.value <= min.value);

const plusDisabledByValue = computed(() => model.value !== undefined && model.value >= max.value);

const formattedValue = computed(() => (model.value != null ? tn(model.value, 'currency') : ''));

// METHODS
const onUpdatePrice = (steps: number): void => {
    const unlimitedValue = saleSteppedPrice.value.getDeltaStepsAmount(saleSteppedPrice.value.getStepRoundedReservePrice(model.value ?? 0), steps);

    model.value = Math.min(Math.max(unlimitedValue, min.value), max.value);
};
</script>

<template>
    <div class="flex flex-1 items-center justify-between">
        <UButton icon="lucide:minus" variant="subtle" :disabled="disabled || minusDisabledByValue" size="sm" @click="onUpdatePrice(-1)" />
        <span class="text-primary text-3xl font-semibold">{{ formattedValue }}</span>
        <UButton icon="lucide:plus" variant="subtle" :disabled="disabled || plusDisabledByValue" size="sm" @click="onUpdatePrice(1)" />
    </div>
</template>
